/**
 * Button — the one every action in five portals goes through.
 *
 * `asChild` renders the variants onto a child element instead of a `<button>`,
 * which is how a link gets to look like a button without a nested-interactive
 * accessibility problem.
 */
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from './cn'

const button = cva(
  [
    // 5px radius and semibold, from the brand reference (1 Oct 2026): squarer
    // than the old `rounded-lg`, which reads as editorial rather than as an app.
    'inline-flex items-center justify-center gap-2 rounded-[5px] font-semibold',
    'transition-colors select-none',
    'disabled:pointer-events-none disabled:opacity-55',
    // The icon in a button is decorative and must not be a hit target of its own.
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-600 text-fg-onbrand hover:bg-brand-700',
        secondary: 'border border-line-strong bg-surface text-fg hover:bg-sunken',
        // ⚠️ `bg-hover`, not `bg-sunken`: a ghost sits on whatever ground its
        // parent paints, and the header *is* sunken — a sunken hover there is
        // invisible. The hover token is a translucent ink that reads on any ground.
        ghost: 'text-fg hover:bg-hover',
        // ⚠️ `fg-ondanger`, not `fg-onbrand`. In dark mode the danger fill is a
        // light orange-red that white cannot sit on (2.6:1), so its text flips
        // to the ground colour. That flip is also what keeps "Delete" from
        // looking like "Publish": wine-with-white beside orange-with-ink.
        danger: 'bg-danger text-fg-ondanger hover:opacity-90',
        // `brand-fg`, not `brand-600`: 600 is a fill in dark mode and measures
        // 2.47:1 as text on the dark ground. See palette.css.
        link: 'text-brand-fg underline-offset-4 hover:underline',
        // For a wine band only (the homepage's closing call to action): white
        // with wine text, the reference's inverted button. ⚠️ Its text is
        // `fg-oninverse`, never `brand-fg` — that is a light wine in dark mode
        // and would vanish on the white. Same colours in both schemes, because
        // the band and the white under the text do not change. Hover tints to
        // the band's pink (`inverse-hover`, a fill token — not the eyebrow's
        // `fg-onbrand-muted` text token): wine on it measures 6.03:1 (light),
        // 7.14:1 (dark).
        // ⚠️ **Its focus ring is white, not the brand gold.** The ring sits on
        // the wine band (it is offset off the button), and gold #b58620 on
        // #85162c measures 2.98:1 — under 3:1. White measures 9.77:1 / 7.40:1.
        inverse:
          'bg-fg-onbrand text-fg-oninverse hover:bg-inverse-hover focus-visible:outline-fg-onbrand',
      },
      size: {
        sm: 'h-8 px-3 text-sm [&_svg]:size-4',
        md: 'h-10 px-4 text-sm [&_svg]:size-4',
        lg: 'h-12 px-6 text-base [&_svg]:size-5',
        icon: 'size-10 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof button> & {
    asChild?: boolean
    /**
     * Shows a spinner and disables the button.
     *
     * Part of the component rather than each caller's problem: a submit button
     * that stays clickable during its request is how duplicate orders happen.
     */
    loading?: boolean
  }

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  /**
   * `asChild` takes the child exactly as given.
   *
   * Radix's `Slot` requires a single element child, and adding the spinner
   * alongside it makes two — which throws at render, not at build. So `loading`
   * applies to real buttons only. That is no loss: `asChild` exists to style a
   * link, and a link has no pending state to show.
   */
  if (asChild) {
    return (
      <Slot className={cn(button({ variant, size }), className)} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      className={cn(button({ variant, size }), className)}
      disabled={disabled ?? loading}
      // Announce the wait without removing the button from the tree, which
      // would move focus and lose the user's place.
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 aria-hidden="true" className="animate-spin" /> : null}
      {children}
    </button>
  )
}
